import { useState, useEffect } from 'react'
import { Tabs, Button, message } from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Editor from '@/components/common/Editor'
import { rubricApi } from '../services/rubric'

const Rubric = () => {
  const [activeTab, setActiveTab] = useState('overview')
  const [overviewContent, setOverviewContent] = useState('')
  const [evaluationContent, setEvaluationContent] = useState('')
  
  const queryClient = useQueryClient()

  // Fetch rubric data
  const { data: rubricData, isLoading } = useQuery({
    queryKey: ['rubric'],
    queryFn: rubricApi.getRubric,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Update rubric mutation
  const updateRubricMutation = useMutation({
    mutationFn: ({ overview, evaluation }: { overview: string; evaluation: string }) =>
      rubricApi.updateRubric(overview, evaluation),
    onSuccess: (response) => {
      message.success(response.message || 'Rubric updated successfully')
      queryClient.invalidateQueries({ queryKey: ['rubric'] })
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update rubric')
    },
  })

  // Initialize content when data is loaded
  useEffect(() => {
    if (rubricData?.data) {
      setOverviewContent(rubricData.data.overview || '')
      setEvaluationContent(rubricData.data.evaluation || '')
    }
  }, [rubricData])

  const handleSave = () => {
    updateRubricMutation.mutate({
      overview: overviewContent,
      evaluation: evaluationContent,
    })
  }

  const handleOverviewChange = (data: string) => {
    setOverviewContent(data)
  }

  const handleEvaluationChange = (data: string) => {
    setEvaluationContent(data)
  }

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <div className="">
          <div className="space-y-20">
            <Editor 
              value={overviewContent}
              onChange={handleOverviewChange} 
            />

            <div className="flex justify-end">
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={updateRubricMutation.isPending}
                className="bg-primary hover:bg-primary/90"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'evaluation',
      label: 'Evaluation',
      children: (
        <div className="space-y-20">
          <Editor 
            value={evaluationContent}
            onChange={handleEvaluationChange} 
          />
          
          <div className="flex justify-end">
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={updateRubricMutation.isPending}
              className="bg-primary hover:bg-primary/90 !h-9"
            >
              Save Changes
            </Button>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">
          Rubric
          {isLoading && <span className="text-sm text-gray-500 ml-2">(Loading...)</span>}
        </h1>
        
        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="rubric-tabs flex-1"
          tabBarGutter={32}
          tabBarStyle={{
            color: "#010ED0", // inactive tab text color
          }}
          moreIcon={null}
          // Use AntD v5 token system for primary color, or override with CSS below
        />
     

        
          
      </div>
    </div>
  )
}

export default Rubric
